import CustomApplicationState from "@crescentParts/Store/CustomApplicationState";
import setRelatedProductsModalIsOpen from "@crescentParts/Store/Data/CustomRelatedProductsOnCart/Handler/RelatedProductsModalHandler";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    openModalIfCMSView = "openModalIfCMSView"
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.openModalIfCMSView]: boolean;
    },
    extendedStyles?: AddToListModalStyles;
    id: string;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomApplicationState) => ({
    session: state.context.session,
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    modalIsOpen: state.cpeState.relatedProductsCustomModal.modalIsOpen,
    location: getLocation(state),
});

const mapDispatchToProps = {
    setRelatedProductsModalIsOpen
};

export interface AddToListModalStyles {
    modal?: ModalPresentationProps;
    innerWrapper?: InjectableCss;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    addButton?: ButtonPresentationProps;
    saveButton?: ButtonPresentationProps;
    confirmationModal?: ModalPresentationProps;
}

export const addToListModalStyles: AddToListModalStyles = {
    modal: {
        size: 1200,
        sizeVariant: "large",
        cssOverrides: {
            modalBody: css`
                // overflow-y: visible;
                // max-height: calc(100% - 0px) !important;
                height: 100% !important; 
            `,
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
            `,
        },
    },
    innerWrapper: {
        css: css`
        &:first-child > div:first-child > div:first-child {
            padding: 0px 20px !important;
        }
        ul[class^="PopoverStyle"] {
            max-height: 150px !important;
        }     
        `,
    },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            text-align: right;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    addButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    saveButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    confirmationModal: {
        sizeVariant: "small",
        cssOverrides: {
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
            `,
        },
    }
};

export const LastUpdatedListIdCookieName = "LastUpdatedListId";

const RelatedProduct = ({
    id,
    fields,
    session,
    extendedStyles,
    modalIsOpen,
    location,
    setRelatedProductsModalIsOpen
}: Props) => {
    const [styles] = useState(() => mergeToNew(addToListModalStyles, extendedStyles));
    const toasterContext = useContext(ToasterContext);
    const [fieldModalOpenFlag, setFieldModalOpenFlag] = useState(fields.openModalIfCMSView);
    const [confirmationModalOpen, setConfirmationModalOpen] = React.useState(false);
    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;

    useEffect(() => {
        setFieldModalOpenFlag(fields.openModalIfCMSView);
    }, [fields.openModalIfCMSView]);

    const modalCloseHandler = () => {
        setRelatedProductsModalIsOpen({ modalIsOpen: false, productId: "" });
        setFieldModalOpenFlag(false);
    };

    useEffect(() => {
        return () => {
            setRelatedProductsModalIsOpen({ modalIsOpen: false, productId: "" });
        }
    },[location])

    if (!isAuthenticated) {
        return null;
    }
    return (
        <>
            <Modal
                {...styles.modal}
                headline={translate("Related Products")}
                isOpen={fieldModalOpenFlag || modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <StyledWrapper {...styles.innerWrapper} data-test-selector="relatedProductsModal">
                    <Zone contentId={id} zoneName="related-products-modal" />
                </StyledWrapper>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RelatedProduct),
    definition: {
        group: "Basic",
        displayName: "Related Product Modal",
        fieldDefinitions: [
            {
                name: "openModalIfCMSView",
                displayName: "Open Modal (Use this check to view modal only, do not save as checked)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            }
        ],
    },
};

export default widgetModule;
